import styled from "styled-components";
import { Button } from "@/common/components/button";
import Table from "@/common/components/Table";
import { Modal } from "@/common/components/modal";
import { useCallback, useMemo, useState } from "react";
import { BsQrCodeScan } from "react-icons/bs";
import { TbHandMove } from "react-icons/tb";
import { H3 } from "@/common/elements";
import Form from "@/common/components/form/Form";
import { KeyValueObj } from "@/common/types";
import { useNotification } from "../Notifications/NotificationProvider";

// ── Styles ────────────────────────────────────────────────────────────────────

const ScannerWrapper = styled.div`
  width: 100%;
  padding: 2rem 0rem;
  background-color: white;
`;

const ScannerModal = styled.div`
  padding: 2rem 4rem;
  background-color: ${({ theme }) => theme.bg.prim_500};

  align-items: center;
  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4rem;
  }

  .icon-wrapper {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 1rem;
    transition: opacity 0.35s ease;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text.primary};
    margin-bottom: 3rem;
  }

  .icon-wrapper.dimmed {
    opacity: 0.3;
  }

  svg {
    cursor: pointer;
    transition: color 0.3s ease;
    &:hover {
      color: ${({ theme }) => theme.text.prim_200};
    }
  }
`;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ScannedItem extends KeyValueObj {
  id: string;
}

interface ScanCollectorProps {
  tableHeaders: (string | JSX.Element)[];
  tableKeys: string[];
  onScanSerial: (serial: string) => Promise<ScannedItem[]>;
  onProceed: (items: ScannedItem[]) => void;
  proceedLabel?: string;
  scanButtonLabel?: string;
  modalTitle?: string;
  loadingProceed?: boolean;

  extraDropDownOptions?: {
    label: string;
    action: (row: KeyValueObj) => void;
  }[];
}

enum ActiveSelection {
  SCAN = "scan",
  TYPE = "type",
}

const SCANNER_OPTIONS = [
  { icon: BsQrCodeScan, title: "Scanner", type: ActiveSelection.SCAN },
  {
    icon: TbHandMove,
    title: "Serial number",
    type: ActiveSelection.TYPE,
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const Scanner = ({
  tableHeaders,
  tableKeys,
  onScanSerial,
  onProceed,
  proceedLabel = "Proceed",
  scanButtonLabel = "Scan",
  modalTitle = "Scan or type Serial number",
  loadingProceed = false,
  extraDropDownOptions = [],
}: ScanCollectorProps) => {
  const [openScan, setOpenScan] = useState(false);
  const [__activeSelection, setActiveSelection] = useState<string | null>(null);
  const [searchingScan, setSearchingScan] = useState(false);
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [, setCurrentRow] = useState<KeyValueObj>({});
  const notify = useNotification();

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleCloseModal = useCallback(() => {
    setActiveSelection(null);
    setOpenScan(false);
  }, []);

  const handleDropdownClick = useCallback(
    (row: KeyValueObj) => {
      setShowDropdown((prev) => !prev);
      setCurrentRow((prev) =>
        prev?.id === row?.id && showDropdown ? {} : row,
      );
    },
    [showDropdown],
  );

  const handleRemoveItem = useCallback((data: KeyValueObj) => {
    setScannedItems((prev) => prev.filter((item) => item?.id !== data?.id));
    setShowDropdown(false);
    setCurrentRow({});
  }, []);

  const [formKey, setFormKey] = useState(0);
  const handleScanSubmit = useCallback(
    async (value: KeyValueObj) => {
      setSearchingScan(true);
      try {
        const resolvedItems = await onScanSerial(value?.serial);
        setScannedItems((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = resolvedItems.filter((item) => {
            if (existingIds.has(item.id)) {
              notify({
                type: "ERROR",
                message: `Item ${item.id} has already been scanned`,
              });
              return false;
            }
            return true;
          });
          return [...prev, ...newItems];
        });
        setShowDropdown(false);

        //Reset the form after every successful scan to avoid multiple scanning of a single item
        setFormKey((prev) => prev + 1);
      } finally {
        setSearchingScan(false);
      }
    },
    [onScanSerial, notify],
  );

  const dropDownOptions = useMemo(
    () => [
      { label: "Delete", action: handleRemoveItem },
      ...extraDropDownOptions,
    ],
    [handleRemoveItem, extraDropDownOptions],
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <ScannerWrapper>
      <div className="top-wrapper">
        <Button onClick={() => setOpenScan(true)} primary>
          {scanButtonLabel}
        </Button>
      </div>

      <Table
        tableHeaders={tableHeaders}
        tableKeys={tableKeys}
        tableData={scannedItems}
        removeFilters
        showDropDown={showDropdown}
        dropDownOptions={dropDownOptions}
        handleDropDownClicked={handleDropdownClick}
        // actionsData={currentRow}
      />
      <h4 className="total">
        Total Quantity Selected:{" "}
        <span className="total-value">{scannedItems?.length}</span>
      </h4>
      <div className="cta">
        <Button
          disabled={scannedItems.length === 0}
          onClick={() => onProceed(scannedItems)}
          primary
          loading={loadingProceed}
        >
          {proceedLabel}
        </Button>
      </div>

      <Modal isOpen={openScan} close={handleCloseModal}>
        <ScannerModal>
          <h2>{modalTitle}</h2>
          <div className="icon-container">
            {SCANNER_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <div className="icon-wrapper">
                  <Icon
                    size={50}
                    onClick={() => setActiveSelection(option.type)}
                  />
                  <H3>{option.title}</H3>
                </div>
              );
            })}
          </div>

          <Form
            key={formKey}
            formInputs={[
              {
                inputType: "text",
                prompt: "Serial Number",
                name: "serial",
                placeholder: "xxxxxx2342",
                fullWidth: true,
              },
            ]}
            processInputs={handleScanSubmit}
            loadingState={searchingScan}
          />
        </ScannerModal>
      </Modal>
    </ScannerWrapper>
  );
};

export default Scanner;
